import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getPliDashboard,
  sendPliReminder,
  requestPli,
  pliStatusBadge,
} from "../services/pliService";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

const CARDS = [
  { key: "", label: "All", color: "#0b2545" },
  { key: "active", label: "Valid", color: "#1a9c4c" },
  { key: "due_soon", label: "Due soon", color: "#e0a800" },
  { key: "lapsed", label: "Lapsed", color: "#d33" },
  { key: "missing", label: "Missing", color: "#888" },
];

const Badge = ({ text, color }) => (
  <span style={{ background: color, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{text}</span>
);

const PliDashboard = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPliDashboard({ status: statusFilter, search });
      if (res.success) {
        setRows(res.data || []);
        setCounts(res.counts || {});
      } else {
        toast.error(res.message || "Failed to load PLI dashboard");
      }
    } catch (e) {
      toast.error(e.message || "Failed to load PLI dashboard");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (row) => {
    setBusyId(row.contractor_id);
    try {
      const res = row.pli_status === "missing"
        ? await requestPli(row.contractor_id)
        : await sendPliReminder(row.contractor_id);
      if (res.success) toast.success(row.pli_status === "missing" ? "PLI request sent" : "Reminder sent");
      else if (res.rateLimited || res.status === 429) toast.warn(res.message || "Emailed today — try again tomorrow");
      else toast.error(res.message || "Action failed");
      await load();
    } catch (e) {
      toast.error(e.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    const head = ["Contractor", "Ref", "Email", "Expiry", "Status", "Overdue"];
    const lines = rows.map((r) =>
      [r.name, r.ref, r.email, r.pli_expiry_date || "", r.pli_status, r.overdue ? "yes" : "no"]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pli-status.csv";
    a.click();
  };

  return (
    <div style={{ padding: 24, fontFamily: "Arial, sans-serif" }}>
      <h2>PLI Status Dashboard</h2>

      {/* status cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "16px 0" }}>
        {CARDS.map((c) => (
          <button key={c.key} onClick={() => setStatusFilter(c.key)}
            style={{
              border: statusFilter === c.key ? `2px solid ${c.color}` : "1px solid #ddd",
              background: "#fff", borderRadius: 8, padding: "12px 18px", cursor: "pointer", minWidth: 110, textAlign: "left",
            }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: c.color }}>
              {c.key === "" ? Object.values(counts).reduce((a, b) => a + b, 0) : (counts[c.key] || 0)}
            </div>
            <div style={{ color: "#555" }}>{c.label}</div>
          </button>
        ))}
      </div>

      {/* controls */}
      <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "center" }}>
        <input placeholder="Search name or ref…" value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ padding: 8, minWidth: 260 }} />
        <button onClick={exportCsv} style={{ padding: "8px 14px" }}>Export CSV</button>
      </div>

      {/* table */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "2px solid #eee" }}>
            <th style={{ padding: 8 }}>Contractor</th>
            <th style={{ padding: 8 }}>Ref</th>
            <th style={{ padding: 8 }}>Expiry</th>
            <th style={{ padding: 8 }}>Status</th>
            <th style={{ padding: 8 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} style={{ padding: 16 }}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={5} style={{ padding: 16, color: "#888" }}>No contractors match.</td></tr>
          ) : (
            rows.map((r) => {
              const b = pliStatusBadge(r.pli_status);
              return (
                <tr key={r.contractor_id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: 8 }}>
                    <a href={`/contractors/${r.contractor_id}`}>{r.name}</a>
                  </td>
                  <td style={{ padding: 8 }}>{r.ref}</td>
                  <td style={{ padding: 8 }}>{fmt(r.pli_expiry_date)}</td>
                  <td style={{ padding: 8 }}>
                    <Badge {...b} />{r.overdue && <>&nbsp;<Badge text="Overdue" color="#d33" /></>}
                  </td>
                  <td style={{ padding: 8 }}>
                    {r.pli_status !== "active" && (() => {
                      // A request is blocked while an earlier PLI ticket is still open.
                      const blockedByTicket = r.pli_status === "missing" && !!r.open_ticket_id;
                      return (
                        <button
                          disabled={busyId === r.contractor_id || r.pli_email_sent_today || blockedByTicket}
                          title={
                            blockedByTicket
                              ? `A PLI ticket (#${r.open_ticket_id}) is already open — complete it before sending another request`
                              : r.pli_email_sent_today
                              ? "Emailed today — try again tomorrow"
                              : ""
                          }
                          onClick={() => act(r)} style={{ padding: "6px 10px" }}>
                          {r.pli_status === "missing" ? "Send PLI request" : "Send reminder"}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PliDashboard;
